import { effect, Injectable, signal } from '@angular/core';
import {
    BehaviorSubject,
    buffer,
    concat,
    debounceTime,
    delay,
    filter,
    map,
    merge,
    shareReplay,
    skipUntil,
    take
} from 'rxjs';
import {
    PickupPlanningTransportOrdersFilterType,
    TransportOrderGetRequestProps,
    PickupPlanningSingleTransportOrderResponse,
    PickupPlanningGroupedTransportOrderResponse
} from '../../models/planningPageTypes';
import { DEBOUNCE_TIME,
    DEFAULT_DATA_VALUE,
    DEFAULT_TRANSPORT_ORDERS_PAGE_SIZE,
    PICKUP_TRANSPORT_ORDERS_FILTER_VALUES_MAP
} from '../configuration/consts/pickupPlanning';
import { BranchLookupService } from './branchLookupService/branch-lookup.service';
import { CrudPickupPlanningTransportOrdersService } from './crud-pickup-planning-transport-orders.service';
import { Router } from '@angular/router';
import { PLANNING_ROUTE } from '../configuration/consts/routesConsts';

@Injectable({
    providedIn: 'root'
})
export class PickupPlanningTransportOrdersService {
    transportOrdersResponseSubject: BehaviorSubject<PickupPlanningGroupedTransportOrderResponse> =
        new BehaviorSubject<PickupPlanningGroupedTransportOrderResponse>(DEFAULT_DATA_VALUE);
    transportOrdersResponseObservable$ = this.transportOrdersResponseSubject.asObservable();

    transportOrderDateRangeSubject: BehaviorSubject<Record<string, unknown>> = new BehaviorSubject<Record<string, unknown>>({});
    transportOrderDateRangeObservable$ = this.transportOrderDateRangeSubject.asObservable();

    transportOrdersSoftFiltersSubject: BehaviorSubject<PickupPlanningTransportOrdersFilterType> =
        new BehaviorSubject<PickupPlanningTransportOrdersFilterType>(PICKUP_TRANSPORT_ORDERS_FILTER_VALUES_MAP);

    transportOrdersSoftFiltersObservable$ = this.transportOrdersSoftFiltersSubject.asObservable();

    loadNextPageSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    loadNextPageObservable$ = this.loadNextPageSubject.asObservable();

    refreshDataSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    refreshDataObservable$ = this.refreshDataSubject.asObservable();

    private transportOrdersPageSize = DEFAULT_TRANSPORT_ORDERS_PAGE_SIZE;
    public transportOrdersCurrentPageIndex = 0;

    transportOrdersWithLoadedGraphs = signal<number[] | null>(null);
    loadingSingleTransportOrderId = signal<number | null>(null);
    singleTransportOrderUpdated = signal<PickupPlanningSingleTransportOrderResponse | null>(null);

    constructor(
        private branchLookupService: BranchLookupService,
        private pickupPlanningTransportOrdersActionsService: CrudPickupPlanningTransportOrdersService,
        private router: Router
    ) {
        const pickupPlanningTransportOrdersBranchChanged$ = this.branchLookupService.branchSelectionChangedObservable$
            .pipe(filter(() => this.router.url.includes(PLANNING_ROUTE)));

        const mergedLoaderObservables$ =  merge(
            this.transportOrderDateRangeObservable$,
            this.transportOrdersSoftFiltersObservable$,
            pickupPlanningTransportOrdersBranchChanged$,
            this.pickupPlanningTransportOrdersActionsService.refreshTransportOrdersObservable$,
            this.refreshDataObservable$
        );

        const trigger$ = this.branchLookupService.branchOptionsObservable$.pipe(
            filter((branchLookup) => !!branchLookup && branchLookup.options && branchLookup.options.length > 0),
            take(1),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        const bufferedEvents$ = mergedLoaderObservables$.pipe(
            buffer(trigger$),
            take(1),
            map(() => null)
        );

        const liveEvents$ = mergedLoaderObservables$.pipe(
            skipUntil(trigger$),
            debounceTime(DEBOUNCE_TIME),
        );

        concat(bufferedEvents$, liveEvents$).subscribe(() => {
            this.transportOrdersResponseSubject.next({
                totalRecordsCount: 0,
                records: [],
                pinned: []
            });
            this.setTransportOrdersCurrentPageIndex(0);
            this.pickupPlanningTransportOrdersActionsService.handleGetTransportOrdersRequest(true)(this.getRequestProps());
        });

        this.loadNextPageObservable$.subscribe(() => this.pickupPlanningTransportOrdersActionsService.handleGetTransportOrdersRequest(false)(this.getRequestProps()));

        effect(() => {
            const transportOrderId = this.loadingSingleTransportOrderId();

            if (transportOrderId) {
                this.pickupPlanningTransportOrdersActionsService.handleGetSingleTransportOrdersRequest(
                    transportOrderId,
                    this.updateSingleTransportOrder,
                    this.stopLoading
                );
            }
        });
    }

    getRequestProps = (): TransportOrderGetRequestProps => ({
        startDateValue: this.transportOrderDateRangeSubject.value['startDate'] as Date,
        endDateValue: this.transportOrderDateRangeSubject.value['endDate'] as Date,
        currentPageIndex: this.transportOrdersCurrentPageIndex,
        pageSize: this.transportOrdersPageSize,
        softFiltersValues: this.transportOrdersSoftFiltersSubject.value,
        responseSubject: this.transportOrdersResponseSubject
    });

    getTransportOrdersCurrentPageIndex = () => this.transportOrdersCurrentPageIndex;
    setTransportOrdersCurrentPageIndex = (index: number) => this.transportOrdersCurrentPageIndex = index;

    get isLoadingSubject() {
        return this.pickupPlanningTransportOrdersActionsService.isLoadingSubject;
    };

    stopLoading = () => {
        this.loadingSingleTransportOrderId.set(null);
    };

    updateSingleTransportOrder = (transportOrder: PickupPlanningSingleTransportOrderResponse) => {
        this.singleTransportOrderUpdated.set(transportOrder);
    };

    toggleExpandedIds(ids: number[]): void {
        const currentExpandedIds = this.transportOrdersWithLoadedGraphs() || [];

        let newExpandedIds: number[];

        newExpandedIds = Array.from(new Set([...currentExpandedIds, ...ids]));

        this.transportOrdersWithLoadedGraphs.set(newExpandedIds);
    }
}